import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/es';
import { TrendingUp, DollarSign, Activity, Users as UsersIcon, ChevronDown, ChevronRight, Download, RefreshCcw, Target, CheckCircle2, CalendarClock, Receipt, AlertCircle } from 'lucide-react';
import { MasterReportDialog } from '../components/MasterReportDialog';
import { ConsultoraProjectionDialog } from '../components/ConsultoraProjectionDialog';
import { MonthYearPicker, monthRange } from '../components/MonthYearPicker';
import { ProjectionSummaryCards } from '../components/ProjectionSummaryCards';

moment.locale('es');
const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLP = (n) => {
  if (n === null || n === undefined) return '—';
  const num = Number(n) || 0;
  return num.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
};

const StatCard = ({ label, value, sublabel, tone = 'default', testid }) => {
  const tones = {
    default: 'bg-white border-[#E8E5DD]',
    green: 'bg-emerald-50 border-emerald-200',
    yellow: 'bg-amber-50 border-amber-200',
    red: 'bg-red-50 border-red-200',
    blue: 'bg-blue-50 border-blue-200',
  };
  return (
    <div className={`rounded-xl border p-5 ${tones[tone]}`} data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-[#6A736B] font-semibold">{label}</p>
      <p className="text-3xl font-bold text-[#1A1C18] mt-2">{value}</p>
      {sublabel && <p className="text-xs text-[#6A736B] mt-1">{sublabel}</p>}
    </div>
  );
};

const PaymentBadge = ({ status }) => {
  const map = {
    'Pagado Completo': 'bg-emerald-100 text-emerald-700 border-emerald-300',
    'Abono': 'bg-amber-100 text-amber-700 border-amber-300',
    'Pendiente': 'bg-red-100 text-red-700 border-red-300',
  };
  const cls = map[status] || 'bg-[#F3EBE1] text-[#6A736B] border-[#E8E5DD]';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`} data-testid="payment-badge">
      {status || 'Sin info'}
    </span>
  );
};

const DoctorRow = ({ doctor, dateRange }) => {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !detail) {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (dateRange.start) params.set('start_date', dateRange.start);
        if (dateRange.end) params.set('end_date', dateRange.end);
        const { data } = await axios.get(`${API_URL}/api/management/doctor/${doctor.doctor_id}/surgeries?${params}`);
        setDetail(data);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    }
  };

  return (
    <>
      <tr
        className="border-b border-[#E8E5DD] hover:bg-[#FDFBF7] cursor-pointer"
        onClick={toggle}
        data-testid={`doctor-row-${doctor.doctor_id}`}
      >
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: doctor.color }} />
            <span className="font-semibold text-[#1A1C18]">{doctor.doctor_name}</span>
          </div>
        </td>
        <td className="py-3 px-4 text-center font-bold text-[#1A1C18]">{doctor.total}</td>
        <td className="py-3 px-4 text-center text-emerald-700 font-semibold">{doctor.operadas}</td>
        <td className="py-3 px-4 text-right font-mono text-[#1A1C18]" data-testid={`doctor-facturado-${doctor.doctor_id}`}>{CLP(doctor.total_facturado)}</td>
        <td className="py-3 px-4 text-right font-mono text-emerald-700 font-semibold" data-testid={`doctor-paid-in-period-${doctor.doctor_id}`}>
          {CLP(doctor.paid_in_period)}
        </td>
        <td className="py-3 px-4 text-right font-mono text-blue-700" data-testid={`doctor-paid-before-${doctor.doctor_id}`}>
          {CLP(doctor.paid_before_period)}
        </td>
        <td className="py-3 px-4 text-right font-mono text-red-700">{CLP(doctor.total_deuda)}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} className="bg-[#FDFBF7] p-4 border-b border-[#E8E5DD]">
            {loading ? (
              <p className="text-sm text-[#6A736B]">Cargando cirugías...</p>
            ) : detail && detail.surgeries.length > 0 ? (
              <div className="overflow-x-auto" data-testid={`doctor-detail-${doctor.doctor_id}`}>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#6A736B] border-b border-[#E8E5DD]">
                      <th className="py-2 px-3 font-medium">Fecha</th>
                      <th className="py-2 px-3 font-medium">Paciente</th>
                      <th className="py-2 px-3 font-medium">Cirugía</th>
                      <th className="py-2 px-3 font-medium">Estado</th>
                      <th className="py-2 px-3 font-medium">Pago</th>
                      <th className="py-2 px-3 font-medium text-right">Abonado</th>
                      <th className="py-2 px-3 font-medium text-right">Total</th>
                      <th className="py-2 px-3 font-medium text-right">Deuda</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.surgeries.map((s) => (
                      <tr key={s.id} className="border-b border-[#E8E5DD]/50 last:border-0">
                        <td className="py-2 px-3 font-mono text-xs">{s.date} · {s.time}</td>
                        <td className="py-2 px-3 font-medium">{s.patient_name}</td>
                        <td className="py-2 px-3">{s.surgery_type}</td>
                        <td className="py-2 px-3">{s.state}</td>
                        <td className="py-2 px-3"><PaymentBadge status={s.atencion_estado_pago} /></td>
                        <td className="py-2 px-3 font-mono text-right text-emerald-700">{CLP(s.atencion_abonado)}</td>
                        <td className="py-2 px-3 font-mono text-right">{CLP(s.atencion_total)}</td>
                        <td className="py-2 px-3 font-mono text-right text-red-700">{CLP(s.atencion_deuda)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-[#6A736B]">Sin cirugías en el período seleccionado.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

export default function Management() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [doctorFilter, setDoctorFilter] = useState('all');
  const [masterReportOpen, setMasterReportOpen] = useState(false);
  const [selectedConsultora, setSelectedConsultora] = useState(null);

  // Selector independiente para la Proyección por Consultora
  // (permite navegar meses futuros sin alterar el balance global de arriba).
  const now = moment();
  const [projectionMonth, setProjectionMonth] = useState(now.month() + 1);
  const [projectionYear, setProjectionYear] = useState(now.year());
  const [projectionData, setProjectionData] = useState(null);
  const [projectionLoading, setProjectionLoading] = useState(false);

  const projectionRange = useMemo(
    () => monthRange(projectionMonth, projectionYear),
    [projectionMonth, projectionYear]
  );

  useEffect(() => {
    const fetchProjection = async () => {
      setProjectionLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('start_date', projectionRange.start_date);
        params.set('end_date', projectionRange.end_date);
        const { data: res } = await axios.get(`${API_URL}/api/management/summary?${params}`);
        setProjectionData(res.by_consultora_projection || []);
      } catch (err) {
        console.error(err);
      }
      setProjectionLoading(false);
    };
    fetchProjection();
  }, [projectionRange.start_date, projectionRange.end_date]);

  useEffect(() => {
    fetchSummary();
  }, [dateRange.start, dateRange.end]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.set('start_date', dateRange.start);
      if (dateRange.end) params.set('end_date', dateRange.end);
      const { data: res } = await axios.get(`${API_URL}/api/management/summary?${params}`);
      setData(res);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const filteredPayments = useMemo(() => {
    if (!data) return [];
    return data.payments.filter((p) => {
      if (paymentFilter !== 'all' && p.atencion_estado_pago !== paymentFilter) return false;
      if (doctorFilter !== 'all' && p.doctor !== doctorFilter) return false;
      return true;
    });
  }, [data, paymentFilter, doctorFilter]);

  const uniqueDoctors = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.payments.map((p) => p.doctor).filter(Boolean))).sort();
  }, [data]);

  const setQuickRange = (days) => {
    const end = moment().format('YYYY-MM-DD');
    const start = moment().subtract(days, 'days').format('YYYY-MM-DD');
    setDateRange({ start, end });
  };

  const exportExcel = () => {
    const params = new URLSearchParams();
    if (dateRange.start) params.set('start_date', dateRange.start);
    if (dateRange.end) params.set('end_date', dateRange.end);
    window.open(`${API_URL}/api/export/surgeries.xlsx?${params}`, '_blank');
  };

  const exportMasterReport = () => {
    setMasterReportOpen(true);
  };

  const downloadMasterReport = async (params) => {
    const qs = new URLSearchParams();
    if (params.start_date) qs.set('start_date', params.start_date);
    if (params.end_date) qs.set('end_date', params.end_date);
    window.open(`${API_URL}/api/management/export.xlsx?${qs}`, '_blank');
  };

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [syncStatus, setSyncStatus] = useState(null); // {processed, updated, failed, total, status}

  const pollSyncStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/medilink/refresh-all-payments/status`);
      setSyncStatus(data);
      if (data.status === 'running') {
        setTimeout(pollSyncStatus, 2000);
      } else if (data.status === 'completed') {
        setSyncing(false);
        setSyncMsg(`✓ Completado: ${data.updated} actualizadas · ${data.failed} fallidas · ${data.processed} procesadas`);
        fetchSummary();
      } else if (data.status === 'failed') {
        setSyncing(false);
        setSyncMsg(`✗ Error: ${data.error || 'desconocido'}`);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const syncAllPayments = async () => {
    if (!window.confirm('Esto consultará Medilink para actualizar montos y estados de todas las cirugías con atención vinculada. La sincronización correrá en segundo plano. ¿Continuar?')) return;
    setSyncing(true);
    setSyncMsg('Iniciando sincronización en segundo plano...');
    setSyncStatus(null);
    try {
      const { data: res } = await axios.post(`${API_URL}/api/medilink/refresh-all-payments`);
      if (res.status === 'already_running') {
        setSyncMsg('Ya hay una sincronización en curso — mostrando su progreso...');
      }
      pollSyncStatus();
    } catch (err) {
      setSyncing(false);
      setSyncMsg(`✗ Error: ${err.response?.data?.detail || err.message}`);
    }
  };

  // Al montar, chequear si hay job corriendo (por si el usuario recarga la página)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/medilink/refresh-all-payments/status`);
        if (data.status === 'running') {
          setSyncing(true);
          setSyncStatus(data);
          pollSyncStatus();
        }
      } catch {
        // no-op: si falla el chequeo, el usuario puede iniciar manualmente
      }
    })();
  }, []);

  if (loading && !data) {
    return (
      <div className="p-6 lg:p-8">
        <p className="text-[#6A736B]">Cargando dashboard de gerencia...</p>
      </div>
    );
  }

  const t = data?.totals || {};

  return (
    <div className="p-6 lg:p-8" data-testid="management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1C18] flex items-center gap-3">
            <TrendingUp className="text-[#2B4C3B]" size={32} />
            Gerencia
          </h1>
          <p className="text-[#6A736B] mt-1">Cirugías por doctor y historial de pagos Medilink</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={syncAllPayments}
            disabled={syncing}
            data-testid="mgmt-sync-button"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#2B4C3B] text-[#2B4C3B] rounded-lg hover:bg-[#F3EBE1] text-sm font-medium disabled:opacity-50"
          >
            <RefreshCcw size={16} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Sincronizando...' : 'Sincronizar montos Medilink'}
          </button>
          <button
            onClick={exportExcel}
            data-testid="mgmt-export-button"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#2B4C3B] text-white rounded-lg hover:bg-[#1F3A2C] text-sm font-medium"
          >
            <Download size={16} /> Exportar Excel
          </button>
          <button
            onClick={exportMasterReport}
            data-testid="mgmt-export-master-button"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#2B4C3B] text-[#2B4C3B] rounded-lg hover:bg-[#EDF5F0] text-sm font-medium"
          >
            <Download size={16} /> Reporte Maestro
          </button>
        </div>
      </div>
      {syncMsg && (
        <div className="mb-4 px-4 py-3 bg-[#F3EBE1] border border-[#E8E5DD] rounded-lg text-sm text-[#1A1C18]" data-testid="mgmt-sync-message">
          <p className="font-medium">{syncMsg}</p>
          {syncStatus && syncStatus.status === 'running' && syncStatus.total > 0 && (
            <div className="mt-2">
              <div className="flex justify-between text-xs text-[#6A736B] mb-1">
                <span>Progreso: {syncStatus.processed} de {syncStatus.total}</span>
                <span>{Math.round((syncStatus.processed / syncStatus.total) * 100)}%</span>
              </div>
              <div className="h-2 bg-white rounded-full overflow-hidden border border-[#E8E5DD]">
                <div
                  className="h-full bg-[#2B4C3B] transition-all duration-500"
                  style={{ width: `${(syncStatus.processed / syncStatus.total) * 100}%` }}
                  data-testid="mgmt-sync-progress-bar"
                />
              </div>
              <p className="text-xs text-[#6A736B] mt-1">
                {syncStatus.updated} actualizadas · {syncStatus.failed} fallidas
                {syncStatus.rate_limited_pauses > 0 && (
                  <span className="text-amber-700 font-medium"> · ⚠ {syncStatus.rate_limited_pauses} pausas por rate limit</span>
                )}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-[#E8E5DD] rounded-xl p-4 mb-6 flex flex-wrap items-center gap-3">
        <label className="text-sm text-[#6A736B]">Desde:</label>
        <input
          type="date"
          value={dateRange.start}
          onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
          data-testid="mgmt-start-date"
          className="px-3 py-1.5 border border-[#E8E5DD] rounded text-sm"
        />
        <label className="text-sm text-[#6A736B]">Hasta:</label>
        <input
          type="date"
          value={dateRange.end}
          onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
          data-testid="mgmt-end-date"
          className="px-3 py-1.5 border border-[#E8E5DD] rounded text-sm"
        />
        <div className="flex gap-1 ml-auto">
          <button onClick={() => setQuickRange(7)} className="px-3 py-1.5 text-sm border border-[#E8E5DD] rounded hover:bg-[#F3EBE1]" data-testid="mgmt-range-7d">7 días</button>
          <button onClick={() => setQuickRange(30)} className="px-3 py-1.5 text-sm border border-[#E8E5DD] rounded hover:bg-[#F3EBE1]" data-testid="mgmt-range-30d">30 días</button>
          <button onClick={() => setQuickRange(90)} className="px-3 py-1.5 text-sm border border-[#E8E5DD] rounded hover:bg-[#F3EBE1]" data-testid="mgmt-range-90d">90 días</button>
          <button onClick={() => setDateRange({ start: '', end: '' })} className="px-3 py-1.5 text-sm border border-[#E8E5DD] rounded hover:bg-[#F3EBE1]" data-testid="mgmt-range-all">Todo</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="kpi-total" label="Total Cirugías" value={t.total_surgeries || 0} sublabel={`${t.operadas || 0} operadas`} tone="blue" />
        <StatCard testid="kpi-abonado" label="Total Abonado" value={CLP(t.total_abonado)} sublabel={`de ${CLP(t.total_facturado)} facturado`} tone="green" />
        <StatCard testid="kpi-deuda" label="Deuda Pendiente" value={CLP(t.total_deuda)} sublabel={`${(t.abono || 0) + (t.pendiente_pago || 0)} cirugías`} tone="red" />
        <StatCard testid="kpi-pagado" label="Pagos Completos" value={t.pagado_completo || 0} sublabel={`${t.total_surgeries ? Math.round((t.pagado_completo / t.total_surgeries) * 100) : 0}% del total`} tone="green" />
      </div>

      {/* Por Doctor */}
      <div className="bg-white border border-[#E8E5DD] rounded-xl overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-[#E8E5DD] flex items-center gap-2">
          <UsersIcon size={18} className="text-[#2B4C3B]" />
          <h2 className="text-lg font-semibold text-[#1A1C18]">Cirugías por Doctor</h2>
          <span className="text-sm text-[#6A736B]">({data?.by_doctor.length || 0})</span>
        </div>
        {/* Tarjetas de resumen — totales del período (suma de todos los doctores de la tabla) */}
        <ProjectionSummaryCards rows={data?.by_doctor} idPrefix="doctor" />
        {data?.by_doctor.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="mgmt-doctors-table">
              <thead>
                <tr className="text-left text-[#6A736B] border-b border-[#E8E5DD] bg-[#FDFBF7]">
                  <th className="py-3 px-4 font-semibold">Doctor</th>
                  <th className="py-3 px-4 text-center font-semibold">Total</th>
                  <th className="py-3 px-4 text-center font-semibold text-emerald-700">Operadas</th>
                  <th className="py-3 px-4 text-right font-semibold" title="Total facturado del periodo">Facturado</th>
                  <th className="py-3 px-4 text-right font-semibold text-emerald-700" title="Pagos recibidos DENTRO del periodo seleccionado">Entró este periodo</th>
                  <th className="py-3 px-4 text-right font-semibold text-blue-700" title="Abonos recibidos ANTES del periodo (pago anticipado)">Anticipado</th>
                  <th className="py-3 px-4 text-right font-semibold text-red-700">Saldo pendiente</th>
                </tr>
              </thead>
              <tbody>
                {data.by_doctor.map((d) => (
                  <DoctorRow key={d.doctor_id} doctor={d} dateRange={dateRange} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="p-6 text-sm text-[#6A736B]">Sin cirugías por doctor en el período.</p>
        )}
      </div>

      {/* Proyección por Consultora — cirugías AGENDADAS (no operadas todavía) */}
      <div className="bg-white border border-[#E8E5DD] rounded-xl overflow-hidden mb-6" data-testid="mgmt-consultora-projection-card">
        <div className="px-6 py-4 border-b border-[#E8E5DD] flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            <Target size={18} className="text-[#8B5CF6]" />
            <h2 className="text-lg font-semibold text-[#1A1C18]">Proyección por Consultora</h2>
            <span className="text-sm text-[#6A736B]">({projectionData?.length || 0})</span>
            {projectionLoading && <span className="text-xs text-[#6A736B] italic">actualizando…</span>}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-xs text-[#6A736B] italic hidden xl:block">
              Cirugías agendadas aún NO operadas · filtro independiente
            </p>
            <MonthYearPicker
              month={projectionMonth}
              year={projectionYear}
              onChange={({ month, year }) => {
                setProjectionMonth(month);
                setProjectionYear(year);
              }}
            />
          </div>
        </div>

        {/* Tarjetas de resumen — totales del período (suma de todas las consultoras) */}
        <ProjectionSummaryCards rows={projectionData} idPrefix="consultora" />
        {(projectionData?.length || 0) > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="mgmt-consultora-projection-table">
                <thead>
                  <tr className="text-left text-[#6A736B] border-b border-[#E8E5DD] bg-[#FDFBF7]">
                    <th className="py-3 px-4 font-semibold">Consultora</th>
                    <th className="py-3 px-4 text-center font-semibold text-[#1A1C18] bg-[#F3EBE1]/50" title="Total Cirugías = Realizadas + Agendadas">Total Cirugías</th>
                    <th className="py-3 px-4 text-center font-semibold text-emerald-800 bg-emerald-50/40" title="Cirugías con estado Operado en el período">Cant. Realizadas</th>
                    <th className="py-3 px-4 text-right font-semibold text-emerald-800 bg-emerald-50/40" title="Σ atencion_total de las cirugías operadas">Facturado Realizado</th>
                    <th className="py-3 px-4 text-center font-semibold text-[#8B5CF6]" title="Cirugías agendadas no operadas">Agendadas</th>
                    <th className="py-3 px-4 text-right font-semibold text-[#8B5CF6]" title="Suma del total a facturar de las cirugías agendadas">Venta Bruta</th>
                    <th className="py-3 px-4 text-right font-semibold text-emerald-700" title="Abonos ya ingresados de esas cirugías">Recaudación Real</th>
                    <th className="py-3 px-4 text-right font-semibold text-red-700" title="Venta bruta − Recaudación real">Brecha de Cobro</th>
                    <th className="py-3 px-4 text-right font-semibold text-[#6A736B]" title="% ya recaudado sobre la venta bruta">% Cobrado</th>
                    <th className="py-3 px-4 text-right font-semibold text-[#2B4C3B] bg-[#2B4C3B]/5" title="Monto Total = Facturado Realizado + Venta Bruta">Monto Total</th>
                  </tr>
                </thead>
                <tbody>
                  {projectionData.map((c) => {
                    const pct = c.venta_bruta > 0 ? Math.round((c.recaudacion_real / c.venta_bruta) * 100) : 0;
                    const totalCirugiasRow = (c.realizadas_count || 0) + (c.total_surgeries || 0);
                    const montoTotalRow = (c.facturado_realizado || 0) + (c.venta_bruta || 0);
                    return (
                      <tr key={c.consultant_id} className="border-b border-[#E8E5DD] hover:bg-[#FDFBF7]" data-testid={`consultora-projection-row-${c.consultant_id}`}>
                        <td className="py-3 px-4">
                          <button
                            type="button"
                            onClick={() => setSelectedConsultora(c)}
                            className="flex items-center gap-2 group text-left"
                            data-testid={`consultora-open-detail-${c.consultant_id}`}
                            title="Ver detalle de cirugías agendadas"
                          >
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
                            <span className="font-semibold text-[#1A1C18] group-hover:text-[#8B5CF6] group-hover:underline underline-offset-2 transition-colors">
                              {c.consultant_name}
                            </span>
                          </button>
                        </td>
                        <td className="py-3 px-4 text-center font-bold text-[#1A1C18] bg-[#F3EBE1]/30" data-testid={`consultora-total-cirugias-${c.consultant_id}`}>
                          {totalCirugiasRow}
                        </td>
                        <td className="py-3 px-4 text-center font-bold text-emerald-800 bg-emerald-50/30" data-testid={`consultora-realizadas-${c.consultant_id}`}>
                          {c.realizadas_count || 0}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-emerald-800 font-semibold bg-emerald-50/30" data-testid={`consultora-facturado-realizado-${c.consultant_id}`}>
                          {CLP(c.facturado_realizado)}
                        </td>
                        <td className="py-3 px-4 text-center font-bold text-[#1A1C18]">{c.total_surgeries}</td>
                        <td className="py-3 px-4 text-right font-mono text-[#1A1C18]" data-testid={`consultora-venta-${c.consultant_id}`}>{CLP(c.venta_bruta)}</td>
                        <td className="py-3 px-4 text-right font-mono text-emerald-700 font-semibold" data-testid={`consultora-recaudacion-${c.consultant_id}`}>{CLP(c.recaudacion_real)}</td>
                        <td className="py-3 px-4 text-right font-mono text-red-700 font-semibold" data-testid={`consultora-brecha-${c.consultant_id}`}>{CLP(c.brecha_cobro)}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="inline-flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-[#E8E5DD] rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, pct)}%` }} />
                            </div>
                            <span className="text-xs font-mono text-[#6A736B] w-9 text-right">{pct}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-[#2B4C3B] font-semibold bg-[#2B4C3B]/5" data-testid={`consultora-monto-total-${c.consultant_id}`}>
                          {CLP(montoTotalRow)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#E8E5DD] bg-[#FDFBF7] font-bold">
                    <td className="py-3 px-4 text-[#1A1C18]">Total</td>
                    <td className="py-3 px-4 text-center text-[#1A1C18] bg-[#F3EBE1]/50" data-testid="consultora-total-total-cirugias">
                      {projectionData.reduce((a, c) => a + (c.realizadas_count || 0) + (c.total_surgeries || 0), 0)}
                    </td>
                    <td className="py-3 px-4 text-center text-emerald-800 bg-emerald-50/40" data-testid="consultora-total-realizadas">
                      {projectionData.reduce((a, c) => a + (c.realizadas_count || 0), 0)}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-emerald-800 bg-emerald-50/40" data-testid="consultora-total-facturado-realizado">
                      {CLP(projectionData.reduce((a, c) => a + (c.facturado_realizado || 0), 0))}
                    </td>
                    <td className="py-3 px-4 text-center text-[#1A1C18]">
                      {projectionData.reduce((a, c) => a + c.total_surgeries, 0)}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-[#1A1C18]" data-testid="consultora-total-venta">
                      {CLP(projectionData.reduce((a, c) => a + c.venta_bruta, 0))}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-emerald-700" data-testid="consultora-total-recaudacion">
                      {CLP(projectionData.reduce((a, c) => a + c.recaudacion_real, 0))}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-red-700" data-testid="consultora-total-brecha">
                      {CLP(projectionData.reduce((a, c) => a + c.brecha_cobro, 0))}
                    </td>
                    <td className="py-3 px-4"></td>
                    <td className="py-3 px-4 text-right font-mono text-[#2B4C3B] bg-[#2B4C3B]/5" data-testid="consultora-total-monto-total">
                      {CLP(projectionData.reduce((a, c) => a + (c.facturado_realizado || 0) + (c.venta_bruta || 0), 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <p className="p-6 text-sm text-[#6A736B]">
            {projectionLoading ? 'Cargando…' : 'Sin cirugías agendadas pendientes de operar en el período.'}
          </p>
        )}
      </div>

      {/* Historial de Pagos */}
      <div className="bg-white border border-[#E8E5DD] rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E8E5DD] flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 flex-1">
            <DollarSign size={18} className="text-[#2B4C3B]" />
            <h2 className="text-lg font-semibold text-[#1A1C18]">Historial de Pagos</h2>
            <span className="text-sm text-[#6A736B]">({filteredPayments.length})</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="px-3 py-1.5 border border-[#E8E5DD] rounded text-sm"
              data-testid="mgmt-payment-filter"
            >
              <option value="all">Todos los pagos</option>
              <option value="Pagado Completo">Pagado Completo</option>
              <option value="Abono">Abono</option>
              <option value="Pendiente">Pendiente</option>
            </select>
            <select
              value={doctorFilter}
              onChange={(e) => setDoctorFilter(e.target.value)}
              className="px-3 py-1.5 border border-[#E8E5DD] rounded text-sm"
              data-testid="mgmt-doctor-filter"
            >
              <option value="all">Todos los doctores</option>
              {uniqueDoctors.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
        {filteredPayments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="mgmt-payments-table">
              <thead>
                <tr className="text-left text-[#6A736B] border-b border-[#E8E5DD] bg-[#FDFBF7]">
                  <th className="py-3 px-4 font-semibold">Fecha</th>
                  <th className="py-3 px-4 font-semibold">Paciente</th>
                  <th className="py-3 px-4 font-semibold">Doctor</th>
                  <th className="py-3 px-4 font-semibold">Cirugía</th>
                  <th className="py-3 px-4 font-semibold">Pago</th>
                  <th className="py-3 px-4 text-right font-semibold text-emerald-700">Abonado</th>
                  <th className="py-3 px-4 text-right font-semibold">Total</th>
                  <th className="py-3 px-4 text-right font-semibold text-red-700">Deuda</th>
                  <th className="py-3 px-4 font-semibold">Trans. ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((p) => (
                  <tr key={p.id} className="border-b border-[#E8E5DD] hover:bg-[#FDFBF7]">
                    <td className="py-3 px-4 font-mono text-xs">{p.date} · {p.time}</td>
                    <td className="py-3 px-4">
                      <p className="font-medium text-[#1A1C18]">{p.patient_name}</p>
                      <p className="text-xs text-[#6A736B] font-mono">{p.patient_rut}</p>
                    </td>
                    <td className="py-3 px-4">{p.doctor}</td>
                    <td className="py-3 px-4">
                      <p className="text-[#1A1C18]">{p.surgery_type}</p>
                      <p className="text-xs text-[#6A736B]">{p.pavilion}</p>
                    </td>
                    <td className="py-3 px-4"><PaymentBadge status={p.atencion_estado_pago} /></td>
                    <td className="py-3 px-4 font-mono text-right text-emerald-700 font-semibold">{CLP(p.atencion_abonado)}</td>
                    <td className="py-3 px-4 font-mono text-right">{CLP(p.atencion_total)}</td>
                    <td className="py-3 px-4 font-mono text-right text-red-700">{CLP(p.atencion_deuda)}</td>
                    <td className="py-3 px-4 font-mono text-xs text-[#6A736B]">{p.transaction_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="p-6 text-sm text-[#6A736B]">Sin pagos que coincidan con los filtros.</p>
        )}
      </div>

      <MasterReportDialog
        open={masterReportOpen}
        onClose={() => setMasterReportOpen(false)}
        onDownload={downloadMasterReport}
      />

      <ConsultoraProjectionDialog
        open={!!selectedConsultora}
        consultant={selectedConsultora}
        dateRange={{ start: projectionRange.start_date, end: projectionRange.end_date }}
        onClose={() => setSelectedConsultora(null)}
      />
    </div>
  );
}
